import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Editor } from '@tinymce/tinymce-react'
import { NavBar } from '../../components/NavBar'
import { useAuthStore } from '../../data/useAuthStore'
import {
  createBlogPost,
  updateBlogPost,
  getBlogPostById,
  uploadBlogImage,
  isSlugAvailable,
} from '../../api/blog'
import { generateSlug } from '../../utils/stringUtils'

export const BlogEditorPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const postId = searchParams.get('id')
  const isEditMode = !!postId

  const { isAdmin } = useAuthStore()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null)

  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [content, setContent] = useState('')
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null)
  const [existingCoverImage, setExistingCoverImage] = useState<string | null>(null)
  const [removeCoverImage, setRemoveCoverImage] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [slugError, setSlugError] = useState<string | null>(null)
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false)

  // Load existing post if in edit mode
  useEffect(() => {
    if (postId) {
      loadPost(postId)
    }
  }, [postId])

  // Auto-generate slug from title (only if not manually edited)
  useEffect(() => {
    if (!isSlugManuallyEdited && title && !isEditMode) {
      const newSlug = generateSlug(title)
      setSlug(newSlug)
    }
  }, [title, isSlugManuallyEdited, isEditMode])

  const loadPost = async (id: string) => {
    try {
      setIsLoading(true)
      const post = await getBlogPostById(id)
      if (post) {
        setTitle(post.title)
        setSlug(post.slug)
        setContent(post.contentHtml)
        setExistingCoverImage(post.coverImageUrl)
        setIsSlugManuallyEdited(true) // Don't auto-generate for existing posts
      } else {
        alert('Blog post not found')
        navigate('/dashboard/blog')
      }
    } catch (error) {
      console.error('Error loading blog post:', error)
      alert('Failed to load blog post')
      navigate('/dashboard/blog')
    } finally {
      setIsLoading(false)
    }
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value)
  }

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSlugManuallyEdited(true)
    const newSlug = generateSlug(e.target.value)
    setSlug(newSlug)
    setSlugError(null)
  }

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCoverImage(file)
      setRemoveCoverImage(false)
      // Create preview URL
      const previewUrl = URL.createObjectURL(file)
      setCoverImagePreview(previewUrl)
    }
  }

  const handleRemoveCoverImage = () => {
    setCoverImage(null)
    setCoverImagePreview(null)
    setRemoveCoverImage(true)
  }

  const validateSlug = async (): Promise<boolean> => {
    if (!slug) {
      setSlugError('Slug is required')
      return false
    }

    const available = await isSlugAvailable(slug, postId || undefined)
    if (!available) {
      setSlugError('This slug is already in use')
      return false
    }

    setSlugError(null)
    return true
  }

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Please enter a title')
      return
    }

    if (!slug.trim()) {
      alert('Please enter a slug')
      return
    }

    const editorContent = editorRef.current?.getContent() || ''
    if (!editorContent.trim()) {
      alert('Please enter some content')
      return
    }

    // Validate slug
    const isSlugValid = await validateSlug()
    if (!isSlugValid) {
      return
    }

    try {
      setIsSaving(true)

      if (isEditMode && postId) {
        // Update existing post
        await updateBlogPost(postId, {
          title: title.trim(),
          slug: slug.trim(),
          contentHtml: editorContent,
          coverImage: coverImage,
          removeCoverImage: removeCoverImage,
        })
        alert('Blog post updated successfully!')
      } else {
        // Create new post
        await createBlogPost({
          title: title.trim(),
          slug: slug.trim(),
          contentHtml: editorContent,
          coverImage: coverImage,
        })
        alert('Blog post created successfully!')
      }

      navigate('/dashboard/blog')
    } catch (error) {
      console.error('Error saving blog post:', error)
      alert('Failed to save blog post. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (title || content || coverImage) {
      if (!confirm('Are you sure you want to discard your changes?')) {
        return
      }
    }
    navigate('/dashboard/blog')
  }

  // TinyMCE image upload handler
  const handleImageUpload = (
    blobInfo: { blob: () => Blob; filename: () => string },
    progress: (percent: number) => void
  ): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        progress(10)
        const file = new File([blobInfo.blob()], blobInfo.filename(), {
          type: blobInfo.blob().type,
        })
        progress(30)
        const result = await uploadBlogImage(file)
        progress(100)
        resolve(result.fileUrl)
      } catch (error) {
        console.error('Error uploading image:', error)
        reject('Image upload failed')
      }
    })
  }

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">You must be an admin to access this page.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading blog post...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <NavBar />

      <div className="max-w-5xl mx-auto px-4 py-8 mt-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">
            {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h1>
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave()}
              disabled={isSaving}
              className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : isEditMode ? 'Update Post' : 'Publish Post'}
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl border border-searchBorder p-8 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={handleTitleChange}
              placeholder="Enter blog post title..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-lg"
            />
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
              URL Slug
            </label>
            <div className="flex items-center">
              <span className="text-gray-500 mr-2">/blog/</span>
              <input
                id="slug"
                type="text"
                value={slug}
                onChange={handleSlugChange}
                placeholder="url-friendly-slug"
                className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm ${
                  slugError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {slugError && <p className="mt-1 text-sm text-red-500">{slugError}</p>}
          </div>

          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cover Image (Optional)
            </label>
            <div className="flex items-start gap-4">
              {(coverImagePreview || (existingCoverImage && !removeCoverImage)) && (
                <div className="relative">
                  <img
                    src={coverImagePreview || existingCoverImage || ''}
                    alt="Cover preview"
                    className="w-40 h-24 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    onClick={handleRemoveCoverImage}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageChange}
                  className="hidden"
                  id="cover-image-input"
                />
                <label
                  htmlFor="cover-image-input"
                  className="inline-block px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  {coverImagePreview || existingCoverImage ? 'Change Image' : 'Upload Image'}
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Recommended: 1200x630px for social sharing
                </p>
              </div>
            </div>
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content
            </label>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <Editor
              licenseKey='gpl'
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                onInit={(_evt, editor) => {
                  editorRef.current = editor
                }}
                initialValue={content}
                init={{
                  height: 500,
                  menubar: true,
                  plugins: [
                    'advlist',
                    'autolink',
                    'lists',
                    'link',
                    'image',
                    'charmap',
                    'preview',
                    'anchor',
                    'searchreplace',
                    'visualblocks',
                    'code',
                    'fullscreen',
                    'insertdatetime',
                    'media',
                    'table',
                    'help',
                    'wordcount',
                  ],
                  toolbar:
                    'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'link image | removeformat | help',
                  content_style:
                    'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; font-size: 16px; line-height: 1.6; }',
                  images_upload_handler: handleImageUpload,
                  automatic_uploads: true,
                  file_picker_types: 'image',
                  // Image settings
                  image_caption: true,
                  image_advtab: true,
                  // Paste settings
                  paste_data_images: true,
                  // Link settings
                  link_default_target: '_blank',
                  // Skin and content CSS from self-hosted
                  skin: 'oxide',
                  content_css: 'default',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
